import { useState } from 'react';
import { Input } from '@erp/ui';
import { Button } from '@erp/ui';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@erp/ui';
import { Plus, Check, X } from '@/components/icons';

interface SelectWithCreateProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  noneLabel?: string;
  onCreateNew: (name: string) => Promise<{ id: string } | void>;
  createPlaceholder?: string;
  isPending?: boolean;
}

export function SelectWithCreate({
  value,
  onChange,
  options,
  placeholder,
  noneLabel = '—',
  onCreateNew,
  createPlaceholder = 'Nouveau...',
  isPending,
}: SelectWithCreateProps) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  if (creating) {
    return (
      <div className="flex gap-1">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={createPlaceholder}
          className="flex-1"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (newName.trim()) {
                onCreateNew(newName.trim()).then((result) => {
                  if (result?.id) onChange(result.id);
                  setNewName('');
                  setCreating(false);
                });
              }
            }
            if (e.key === 'Escape') { setCreating(false); setNewName(''); }
          }}
        />
        <Button
          type="button"
          size="icon"
          className="shrink-0 h-8 w-8"
          disabled={!newName.trim() || isPending}
          onClick={async () => {
            const result = await onCreateNew(newName.trim());
            if (result?.id) onChange(result.id);
            setNewName('');
            setCreating(false);
          }}
        >
          <Check size={14} />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="shrink-0 h-8 w-8" onClick={() => { setCreating(false); setNewName(''); }}>
          <X size={14} />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-1">
      <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
        <SelectTrigger className="flex-1">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">{noneLabel}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button type="button" variant="outline" size="icon" className="shrink-0 h-8 w-8" onClick={() => setCreating(true)}>
        <Plus size={14} />
      </Button>
    </div>
  );
}
